import { AuthenticationRootContextProvider, useUserRoutes } from '@cosuno/authentication-frontend';
import {
  LayersProvider,
  LightboxContext,
  MomentContext,
  PopoverContext,
  QueryMatchesContext,
  ScrollLockContext,
  useLightbox,
  useLocalStorageState,
  usePopoverContextValues,
  useQueryMatches,
  useScrollLockManager,
} from '@cosuno/cosuno-ui';
import { FeatureFlagsRootContextProvider } from '@cosuno/feature-flags-frontend';
import { DownloadPreparationContext, useDownloadPreparationManager } from '@cosuno/files-frontend';
import { GoogleMapsApiContext, useGoogleMapsApiManager } from '@cosuno/form';
import { overwriteBrowserLanguage } from '@cosuno/localization';
import { ModalsContext, useModalsManager } from '@cosuno/modal-frontend';
import {
  NavigationConfirmationManagerContext,
  useNavigationConfirmationManager,
} from '@cosuno/react-hooks';
import { UserProfileTabs } from '@cosuno/routes';
import moment from 'moment';
import React, { type PropsWithChildren } from 'react';
import { Provider as BalancerProvider } from 'react-wrap-balancer';

import { AnalyticsRootContextProvider } from '~/packages/Analytics';
import { CombinedNotificationsContextProvider } from '~/shared/components/CombinedNotificationsContext';
import { AnalyticsExperimentsContextProvider } from '~/shared/contexts/analyticsExperimentsContext';
import { RefreshFeatureFlagsContextProvider } from '~/shared/contexts/refreshFeatureFlagsContext';
import UiStateContext from '~/shared/contexts/uiStateContext';
import { env } from '~/shared/utils/env';

const Contexts: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  if (env.REACT_APP_CYPRESS === 'true') {
    overwriteBrowserLanguage(env.REACT_APP_DEFAULT_LANG);
  }
  const routes = useUserRoutes();
  const modalManagerProps = useModalsManager();
  const queryMatches = useQueryMatches();
  const downloadPreparationManagerMethods = useDownloadPreparationManager({
    internalApiUrl: `${env.REACT_APP_API_BASE}/internal`,
  });
  const scrollLockMethods = useScrollLockManager();
  const lightbox = useLightbox();
  const navigationConfirmationManager = useNavigationConfirmationManager();
  const getGoogleMapsApi = useGoogleMapsApiManager({
    apiKey: env.REACT_APP_GOOGLE_MAPS_API_KEY,
    mock: env.MODE !== 'production' || env.REACT_APP_CYPRESS === 'true',
  });
  const popoverContextValue = usePopoverContextValues();

  const [isMainNavigationCollapsed, setIsMainNavigationCollapsed] = useLocalStorageState<boolean>(
    'MainNavigation.isCollapsed',
    true,
  );

  return (
    <AnalyticsRootContextProvider>
      <FeatureFlagsRootContextProvider>
        <AuthenticationRootContextProvider
          apiUserEndpoint={`${env.REACT_APP_API_BASE}/internal/auth/user`}
          ipInfoToken={env.REACT_APP_IPINFO_TOKEN}
          reCaptchaKey={
            env.REACT_APP_RECAPTCHA_DISABLED !== 'true' && env.REACT_APP_RECAPTCHA_SITE_KEY
              ? env.REACT_APP_RECAPTCHA_SITE_KEY
              : null
          }
          isTestRun={env.REACT_APP_CYPRESS === 'true'}
          isDevMode={env.MODE === 'development'}
          routes={{
            defaultRedirectToAfterLogin: routes.root(),
            login: routes.login(),
            userLocked: routes.userLocked(),
            premium: routes.premium({ skipWorkspace: true }),
            profileSecurity: routes.profile(UserProfileTabs.security),
          }}
        >
          <BalancerProvider>
            <LayersProvider>
              <MomentContext.Provider value={moment}>
                <ModalsContext.Provider value={modalManagerProps}>
                  <PopoverContext.Provider value={popoverContextValue}>
                    <QueryMatchesContext.Provider value={queryMatches}>
                      <DownloadPreparationContext.Provider
                        value={downloadPreparationManagerMethods}
                      >
                        <ScrollLockContext.Provider value={scrollLockMethods}>
                          <LightboxContext.Provider value={lightbox}>
                            <CombinedNotificationsContextProvider>
                              <NavigationConfirmationManagerContext.Provider
                                value={navigationConfirmationManager}
                              >
                                <GoogleMapsApiContext.Provider value={getGoogleMapsApi}>
                                  <AnalyticsExperimentsContextProvider>
                                    <RefreshFeatureFlagsContextProvider>
                                      <UiStateContext.Provider
                                        value={{
                                          isMainNavigationCollapsed,
                                          setIsMainNavigationCollapsed,
                                        }}
                                      >
                                        {children}
                                      </UiStateContext.Provider>
                                    </RefreshFeatureFlagsContextProvider>
                                  </AnalyticsExperimentsContextProvider>
                                </GoogleMapsApiContext.Provider>
                              </NavigationConfirmationManagerContext.Provider>
                            </CombinedNotificationsContextProvider>
                          </LightboxContext.Provider>
                        </ScrollLockContext.Provider>
                      </DownloadPreparationContext.Provider>
                    </QueryMatchesContext.Provider>
                  </PopoverContext.Provider>
                </ModalsContext.Provider>
              </MomentContext.Provider>
            </LayersProvider>
          </BalancerProvider>
        </AuthenticationRootContextProvider>
      </FeatureFlagsRootContextProvider>
    </AnalyticsRootContextProvider>
  );
};

export default Contexts;
